import { useContext, useState } from "react";
import { useFormik } from "formik";
import AuthService from "../../../Services/authService/AuthService";
import BaseButton from "../../Buttons/BaseButton";
import BaseForm from "../../Form/BaseForm";
import ButtonGroup from "../../Buttons/ButtonGroup";
import Container from "../../Helper/Container.jsx";
import MarginChildren from "../../Helper/MarginChildren.jsx";
import MasterPassword from "./MasterPassword.jsx";
import MasterPasswordMessage from "./MasterPasswordMessage.jsx";
import Page from "../../Helper/Page.jsx";
import registerSchema from "../../Schemas/registerSchema";
import SubmitButton from "../../Buttons/SubmitButton";
import translate from "../../../Utils/translate";
import usePrompt from "../../../Hooks/usePrompt";
import UserContextProvider from "../../../Context/UserContext";
import UserInfo from "./UserInfo";

const USER_INFO = "userInfo";
const MASTER_PASS_MESSAGE = "masterPassword";
const MASTER_PASSWORD = "password";

const Register = ({ setAuthState }) => {
  const formik = useFormik({
    initialValues: {
      confirmPassword: "",
      password: "",
      passwordHint: "",
      recoveryEmail: "",
      username: "",
    },
    validateOnChange: false,
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      await registerUser(values);
    },
  });

  const [errorId, setErrorId] = useState("");
  // 'userInfo', 'masterPassword', 'password'
  const [state, setState] = useState(USER_INFO);
  const { register } = useContext(UserContextProvider);
  const prompt = usePrompt();

  async function handleRegister(event) {
    if (state === USER_INFO) {
      if (formik.errors.username || formik.errors.recoveryEmail) {
        return;
      }
      const usernameIsAvailable = await validateUsername(formik.values?.username);

      if (!usernameIsAvailable) {
        return;
      }

      setState(MASTER_PASS_MESSAGE);
      return null;
    }

    formik.handleSubmit(event);
  }

  async function validateUsername(username) {
    try {
      return await AuthService.validateUsername(username);
    } catch (error) {
      if (error.status === 409) {
        formik.setFieldError("username", translate("errors.username.not_available"));
      }

      prompt.createError(error);

      return null;
    }
  }

  async function registerUser(values) {
    try {
      await register(values);
      prompt.createSuccess(translate("success.register.message"));
      prompt.clearPrompt(errorId);
    } catch (error) {
      const errorId = prompt.createError(error);
      setErrorId(errorId);
    }
  }

  function getFooter() {
    let buttons;

    switch (true) {
      case state === USER_INFO:
        buttons = <SubmitButton text={translate("forms.buttons.next")} />;
        break;

      case state === MASTER_PASS_MESSAGE:
        buttons = (
          <ButtonGroup>
            <BaseButton
              bgColor="default"
              onClick={() => setState(USER_INFO)}
              text={translate("forms.buttons.back")}
              type="button"
            />
            <BaseButton
              onClick={() => setState(MASTER_PASSWORD)}
              text={translate("forms.buttons.next")}
              type="button"
            />
          </ButtonGroup>
        );
        break;

      case state === MASTER_PASSWORD:
        buttons = (
          <ButtonGroup>
            <BaseButton
              bgColor="default"
              onClick={() => setState(MASTER_PASS_MESSAGE)}
              text={translate("forms.buttons.back")}
              type="button"
            />
            <SubmitButton disabled={formik.isSubmitting} text={translate("forms.buttons.register")} />
          </ButtonGroup>
        );
        break;
      default:
        buttons = <SubmitButton text={translate("forms.buttons.next")} />;
    }

    return (
      <footer>
        <p className="w-fit m-auto mb-default text-sm">
          {translate("pages.register.footer.cta")}{" "}
          <span className=" text-theme-accent font-bold underline cursor-pointer" onClick={() => setAuthState("login")}>
            {translate("pages.register.footer.message")}
          </span>
        </p>
        {buttons}
      </footer>
    );
  }

  return (
    <Page>
      <Container className="flex flex-col h-full m-auto md:max-w-screen-sm">
        <div className="flex justify-center">
          <h1 className="my-16 font-semibold text-3xl">{translate("pages.register.header")}</h1>
        </div>

        <BaseForm className="flex-grow flex flex-col justify-between" onSubmit={handleRegister}>
          {state === USER_INFO && <UserInfo formik={formik} />}

          {state === MASTER_PASS_MESSAGE && <MasterPasswordMessage />}

          {state === MASTER_PASSWORD && (
            <MarginChildren>
              <MasterPassword formik={formik} required={true} />
            </MarginChildren>
          )}

          {getFooter()}
        </BaseForm>
      </Container>
    </Page>
  );
};

export default Register;
